/**
 * Helpers for the suggestion-materialisation contract
 * (`MaterializationResult` in `types/api.ts`).
 *
 * These live outside the components so both the panel and the notices list can
 * use them, and so the `source_ref` format sits in exactly one place on this
 * side of the contract.
 */

import type { MaterializedAnnotation, Notice, NoticeKind, NoticeSeverity } from '@/types/api';

// ---------------------------------------------------------------------------
// The closed set of notice kinds
// ---------------------------------------------------------------------------

/**
 * Every `NoticeKind`, as a runtime list, mirroring `backend/app/config/coach_notices.yaml` —
 * the SSOT, which lives on the backend because that is where the sentences are
 * written (REFACTOR_PLAN §C.7).
 *
 * Almost nothing in the UI branches on a kind (cards join to notices by
 * `source_ref`, and the coach-facing sentences all arrive in `what` / `why` /
 * `how`), so an unrecognised kind still renders correctly. The ONE exception
 * is `verbal_target_conflict`: its card needs that specific notice and not the
 * other notes about the same check, so the kind is load-bearing there — see
 * `VERBAL_TARGET_CONFLICT_KIND` in `utils/verbalTargetCopy.ts`, which is the
 * single place that name is written. This list exists to make DRIFT
 * visible: the two `satisfies`/`Exclude` guards below fail to compile if the
 * list and the type ever disagree in either direction, and a test pins the
 * count, so adding a kind to the catalogue without adding it here is caught at
 * merge rather than in front of a coach.
 *
 * Severities are NOT mirrored here on purpose — each notice carries its own, so
 * a local kind→severity map would be a second source of truth. For the record,
 * the catalogue's current assignment is: `dropped` for the fourteen refusals
 * (PR-4 adds `symmetric_region_deferred` / `rotational_region_deferred`),
 * `degraded` for `phase_not_matched` / `duplicate_focus_area` /
 * `standard_strictness_used` / `importance_set_to_standard` and PR-5's
 * `gate_needs_a_number` / `gate_not_possible_for_this_check`, and `info` for
 * `applies_throughout` / `stated_number_used` /
 * `mistake_kept_as_coaching_note` and PR-5's `gate_threshold_source`
 * (PR-7 adds `verbal_target_conflict`, also `info`).
 */
export const NOTICE_KINDS = [
  'unknown_joint',
  'missing_joint',
  'unscorable_body_part',
  'unknown_body_part',
  'unsupported_check_type',
  'incomplete_line',
  'incomplete_order_check',
  'order_not_clear',
  'same_moment_both_parts',
  'angle_out_of_range',
  'side_not_declared',
  'symmetric_region_deferred',
  'rotational_region_deferred',
  'stale_suggestion',
  'phase_not_matched',
  'duplicate_focus_area',
  'standard_strictness_used',
  'importance_set_to_standard',
  'gate_needs_a_number',
  'gate_not_possible_for_this_check',
  'applies_throughout',
  'stated_number_used',
  'mistake_kept_as_coaching_note',
  'gate_threshold_source',
  'verbal_target_conflict',
  // PR-8 — what a check can and cannot be measured on, and the review surface.
  'cannot_measure_camera_angle',
  'cannot_measure_no_reference',
  'cannot_assess_camera_unknown',
  'candidate_not_agreed',
  'duplicate_check',
  'declaration_required',
  'partial_compile_confirm',
] as const satisfies readonly NoticeKind[];

/** Compile-time guard: every `NoticeKind` appears in `NOTICE_KINDS` above. */
type EveryKindListed =
  Exclude<NoticeKind, (typeof NOTICE_KINDS)[number]> extends never ? true : never;
const _everyKindListed: EveryKindListed = true;
void _everyKindListed;

// ---------------------------------------------------------------------------
// Appending what the materialiser returned
// ---------------------------------------------------------------------------

/**
 * Append the materialiser's annotations to a template's annotation list —
 * VERBATIM.
 *
 * This is a one-line function on purpose: it exists so that "the client adds
 * what the server built and changes nothing" is a named rule with a test on it,
 * rather than a habit. The rule is the whole point of PR-3. The code it
 * replaced rebuilt each goal config by hand and wrote three field names the
 * backend model does not have (`target_min_degrees`, `target_max_degrees`,
 * `verbal_target_priority`), so Pydantic dropped them and the angle range a
 * coach said out loud never reached scoring (audit items TV-3 / FE-1 / E2E-3).
 *
 * No re-scoping, no defaulting, no placeholder canvas box, no id rewriting. If
 * something about an annotation needs changing, the materialiser changes it.
 */
export function appendMaterializedAnnotations<T>(
  existing: readonly T[],
  materialized: readonly MaterializedAnnotation[],
): (T | MaterializedAnnotation)[] {
  return [...existing, ...materialized];
}

/**
 * Put the materialiser's annotations IN PLACE OF ones it built earlier —
 * again VERBATIM (PR-7).
 *
 * Why this exists: switching which number a check is judged against
 * (`MaterializeRequest.verbal_target_priority`) re-materialises that one goal.
 * The server returns a rebuilt annotation, and appending it would leave the
 * coach with two copies of the same check — one scored against each number,
 * both counting. So the client replaces, by `id`, and changes nothing else:
 * every other annotation keeps its position, including the boxes and edits the
 * coach has made since.
 *
 * The replacements land at the position of the FIRST annotation they replace,
 * so a check does not jump to the bottom of the list when the coach switches
 * it. Ids that are not present are ignored. When none of them is present —
 * nothing to replace — the annotations are appended, so an annotation can
 * never be silently dropped on the floor.
 */
export function replaceMaterializedAnnotations<T extends { id: string }>(
  existing: readonly T[],
  replacedIds: readonly string[],
  materialized: readonly MaterializedAnnotation[],
): (T | MaterializedAnnotation)[] {
  const replaced = new Set(replacedIds);
  const firstIndex = existing.findIndex((item) => replaced.has(item.id));
  if (firstIndex === -1) return [...existing, ...materialized];

  const next: (T | MaterializedAnnotation)[] = [];
  existing.forEach((item, i) => {
    if (i === firstIndex) next.push(...materialized);
    if (!replaced.has(item.id)) next.push(item);
  });
  return next;
}

// ---------------------------------------------------------------------------
// Which materialised annotation came from which suggestion
// ---------------------------------------------------------------------------

/**
 * Join the annotations a run returned back to the suggestions that were sent —
 * FAIL-CLOSED (PR-7).
 *
 * A `MaterializedAnnotation` carries no `source_ref` (see the contract in
 * `types/api.ts`), so the only case in which the join is a FACT rather than an
 * assumption about response ordering is a run that asked for one item and got
 * one annotation back. That is exactly the per-card Accept and the per-card
 * "switch which number this is judged against", which is where the join is
 * needed.
 *
 * Everything else returns `{}` — no mapping, so the switch action does not
 * offer itself and nothing wrong is shown. After an "Add all" run the coach
 * still READS any conflict, because every notice renders in the run list with
 * its own what / why / how; only the one-click switch is unavailable there.
 *
 * The backend stamps `source_ref` on each materialised annotation (PR-7), so
 * this is a real join by that key; the one-item rule below it is only the
 * fallback for a response that lacks the key. Guessing the order instead would
 * put one check's numbers on another check's card and switch the wrong one.
 */
export function mapAnnotationsToRequestedRefs(
  requestedRefs: readonly string[],
  materialized: readonly MaterializedAnnotation[],
): Record<string, MaterializedAnnotation> {
  // Real join: since PR-7 the backend stamps `source_ref` on every materialised
  // annotation (the same strings the notices carry, compared whole). Use it
  // whenever every returned annotation has one and it is one the caller asked
  // for; a bulk "Add all" then joins every card.
  if (materialized.length > 0 && materialized.every((a) => typeof a.source_ref === 'string')) {
    const wanted = new Set(requestedRefs);
    const joined: Record<string, MaterializedAnnotation> = {};
    for (const annotation of materialized) {
      const ref = annotation.source_ref as string;
      if (wanted.has(ref) && !(ref in joined)) joined[ref] = annotation;
    }
    return joined;
  }
  // Fallback for a response without `source_ref` (an older backend): only a
  // one-item run can be joined without guessing the order.
  if (requestedRefs.length !== 1 || materialized.length !== 1) return {};
  return { [requestedRefs[0]]: materialized[0] };
}

// ---------------------------------------------------------------------------
// source_ref — the join key between a proposed item and its notice
// ---------------------------------------------------------------------------
//
// Format is part of the materialise contract (see `MaterializationResult`,
// contract note 1): the backend builds the same strings from the same
// positions in the matching `TeachingVideoAnalysis` list, and `source_ref` is
// always present. Nothing on this side parses one — it is compared whole.
//
// The frontend only ever SENDS goals and focus areas, so only those two have
// a builder here. The backend can also refuse a phase or a common mistake and
// report it as `coaching_phase:{i}` / `common_mistake:{i}`; those notices have
// no card of their own and render in the run list, which needs no builder.

export function goalSourceRef(index: number): string {
  return `suggested_goal:${index}`;
}

export function emphasisSourceRef(index: number): string {
  return `body_part_emphasis:${index}`;
}

// ---------------------------------------------------------------------------
// verbal_target_priority — which number a goal is judged against (PR-7)
// ---------------------------------------------------------------------------

/**
 * Build `MaterializeRequest.verbal_target_priority` for one run.
 *
 * Keyed by the goal's index AS A STRING, which is the request contract
 * (`types/api.ts`), and restricted to the goals this run is actually about —
 * sending a choice for a goal that is not being materialised would state a
 * preference about a check the server is not touching.
 *
 * Returns `undefined` when there is nothing to say: ABSENT means the number
 * the coach said wins, which is the default, so an empty object would be a
 * second way of writing the same thing.
 */
export function buildVerbalTargetPriority(
  goalIndices: readonly number[],
  choices: Readonly<Record<number, boolean>>,
): Record<string, boolean> | undefined {
  const entries = goalIndices
    .filter((index) => typeof choices[index] === 'boolean')
    .map((index) => [String(index), choices[index]] as const);
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

// ---------------------------------------------------------------------------
// Severity, in the coach's words
// ---------------------------------------------------------------------------

/**
 * What each severity means for the check it is about. These say what HAPPENED;
 * the internal severity token never renders
 * (docs/COACH_FACING_COPY_STANDARD.md §2).
 */
export const SEVERITY_LABELS: Record<NoticeSeverity, string> = {
  dropped: 'Not added',
  degraded: 'Added with a note',
  info: 'Worth a look',
};

export const SEVERITY_VARIANTS: Record<NoticeSeverity, 'error' | 'warning' | 'info'> = {
  dropped: 'error',
  degraded: 'warning',
  info: 'info',
};

/** Most consequential first, so the checks that are missing lead. */
const SEVERITY_ORDER: NoticeSeverity[] = ['dropped', 'degraded', 'info'];

export function sortNoticesBySeverity(notices: Notice[]): Notice[] {
  return [...notices].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity),
  );
}
